/**
 * Sessie 70, punt 13: de kanaalkeuze bovenaan het antwoordvak. Een
 * segment met vier woorden (E-mail, Chat, WhatsApp, Bellen). Een kanaal
 * zonder adres, nummer of open gesprek staat uit, met het woordje waarom
 * zichtbaar eronder en als title. De keuze zelf (welke stand er open gaat)
 * regelt het antwoordvak in `Standen.tsx`.
 */
import { useEffect, useRef, type KeyboardEvent } from "react";

import type { Kanaal, V6KanaalStand } from "../api";
import { Icoon } from "../Icoon";

const WOORD: Record<Kanaal, string> = {
  mail: "E-mail",
  chat: "Chat",
  whatsapp: "WhatsApp",
  telefoon: "Bellen",
};

export function KanaalKeuze({
  kanalen,
  gekozen,
  onKies,
  focusBijStart = false,
}: {
  kanalen: V6KanaalStand[];
  /** Null: nog niets gekozen (geen open gesprek). */
  gekozen: Kanaal | null;
  onKies: (kanaal: Kanaal) => void;
  /** Na een wissel komt de keuze in een nieuwe stand: de focus gaat mee. */
  focusBijStart?: boolean;
}) {
  const groep = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusBijStart) {
      groep.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    }
    // Alleen bij het opbouwen: daarna houdt de knop zelf de focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Zonder keuze staat het eerste kanaal dat aan staat in de Tab-volgorde.
  const inTab = gekozen ?? kanalen.find((k) => k.aan)?.kanaal ?? kanalen[0]?.kanaal;
  // Radiogroep volgens het toetsenbordpatroon: één knop in de Tab-volgorde
  // (de gekozen), pijltjes lopen langs alle vier. Een kanaal dat uit staat
  // is aria-disabled in plaats van disabled: het krijgt focus, zodat de
  // reden voorgelezen wordt, maar kiezen doet niets.
  const pijl = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const stap =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!stap) return;
    e.preventDefault();
    const volgende = (i + stap + kanalen.length) % kanalen.length;
    groep.current?.querySelectorAll<HTMLButtonElement>("button")[volgende]?.focus();
    const doel = kanalen[volgende];
    if (doel.aan && doel.kanaal !== gekozen) onKies(doel.kanaal);
  };
  return (
    <div className="kanaalkeuze" role="radiogroup" aria-label="Kanaal" ref={groep}>
      {kanalen.map((k, i) => {
        const on = k.kanaal === gekozen;
        const uit = !k.aan;
        return (
          <button
            key={k.kanaal}
            type="button"
            role="radio"
            aria-checked={on}
            aria-disabled={uit || undefined}
            tabIndex={k.kanaal === inTab ? 0 : -1}
            aria-label={uit && k.reden ? `${WOORD[k.kanaal]}, ${k.reden}` : WOORD[k.kanaal]}
            title={uit ? (k.reden ?? undefined) : (k.adres ?? WOORD[k.kanaal])}
            className={`${on ? "on" : ""}${uit ? " uit" : ""}`.trim() || undefined}
            onKeyDown={(e) => pijl(e, i)}
            onClick={() => {
              if (!on && !uit) onKies(k.kanaal);
            }}
          >
            <span className="woord">
              <Icoon naam={k.kanaal} klein />
              {WOORD[k.kanaal]}
            </span>
            {uit && k.reden && <small>{k.reden}</small>}
          </button>
        );
      })}
    </div>
  );
}
